import React, { Fragment, Suspense, useCallback, useLayoutEffect, useReducer, useRef, useState, } from 'react';
import { cx } from '@emotion/css';
import { Enum_LiveStream_Status } from '@core/api';
import { isTruthy } from '@core/helpers/boolean';
import { hasCTA } from '@core/helpers/video/hasCTA';
import { useVisibilityObserver } from '@core/hooks/useVisibilityObserver';
import { useContextTranslation } from '@core/i18n';
import debounce from '@core/lodash/debounce';
import { KEY_FIRST_THUMBNAIL_RENDERED, KEY_FIRST_VIDEOS_RECEIVED, usePerformance, } from '@core/performance';
import { PlayTriggersEnum, setPlayTrigger, } from '@core/tracking/playTriggerTracking';
import { useEmbedFeedStore } from '@core/widgets/embedFeed/store';
import { useStore } from '@core/widgets/store';
import { actionsSelector, videosSelector } from '@core/widgets/store/selectors';
import { usePreloadVideo } from '@embed/components/Grid/hooks/usePreloadVideo';
import { useVideoPlayStatus } from '@embed/components/Grid/hooks/useVideoPlayStatus';
import { customEmbedEvent, EventType } from '@embed/customEmbedEvent';
import { useFirstPartyLiveStream } from '@embed/hooks/useFirstPartyLiveStream';
import { useVideoIsLocked } from '@player/features/gatedVideo';
import { EMBED_THUMBNAIL_CONTAINER } from '@player/testIds';
import { ThumbnailBody } from '../ThumbnailBody';
import ThumbnailWrapper from '../ThumbnailWrapper';
import { UseLiveStreamingLite } from '../UseLiveStreamingLite';
import { useThumbnailOnClick } from '../useThumbnailOnClick';
import { THUMBNAIL_AUTOPLAY_DELAY } from './constants';
import { HostWillBeRightBackOverlay, PlayingInPIPOverlay, ThumbnailContainer, ThumbnailPictureByOrientation, } from './Thumbnail.styles';
import { useThumbnailLabel } from './useThumbnailLabel';
/**
 * If a motion lasts more than 5 seconds it must be possible to pause, stop, or hide it.
 * So we limit the duration of autoplay to 5 seconds when params.a11y_limit_previews=true
 * @see https://www.w3.org/WAI/WCAG21/Understanding/pause-stop-hide.html
 */
export const WCAG_MOTION_PREVIEW_DURATION_LIMIT_SECONDS = 5;
const Autoplay = React.lazy(() => import('../Autoplay'));
const reducer = (state, action) => {
    let { impressionTimeout, isInViewport, wasInViewport } = state;
    const { index, total, video, onFeedImpression, registerThumbnailImpression, registerInViewport, unregisterInViewport, trackThumbnailImpressionsEmbedFeed, } = state;
    const { isVisible, fetchMoreVideos } = action;
    const initial = {
        impressionTimeout,
        isInViewport,
        wasInViewport,
    };
    // When last (3) thumbnail is reached, call for more videos
    const countOfNextVideos = total - (index - 1);
    if (countOfNextVideos === 3 && isVisible && !wasInViewport) {
        fetchMoreVideos && fetchMoreVideos(countOfNextVideos);
    }
    // Register thumbnail in viewport
    if (isVisible) {
        if (!isInViewport) {
            // This event is required to be tracked only
            // when at least 50% is in the viewport.
            onFeedImpression();
            registerInViewport(video);
            isInViewport = true;
            wasInViewport = true;
            if (!impressionTimeout) {
                impressionTimeout = setTimeout(() => {
                    // Register thumbnail impression if thumbnail
                    // stays in viewport for at least 1s
                    registerThumbnailImpression(video);
                    // Track thumbnail impression for EmbedFeed
                    trackThumbnailImpressionsEmbedFeed();
                }, 1000);
            }
        }
    }
    else {
        if (isInViewport) {
            unregisterInViewport(video);
            isInViewport = false;
            if (impressionTimeout) {
                impressionTimeout = clearTimeout(impressionTimeout);
            }
        }
    }
    const changes = {
        impressionTimeout,
        isInViewport,
        wasInViewport,
    };
    // Dummy compare of two objects to optimize rerenders
    if (JSON.stringify(initial) !== JSON.stringify(changes)) {
        return {
            ...state,
            ...changes,
        };
    }
    return state;
};
/**
 * This condition is used inside Thumbnail to check if we should render Autoplay component.
 * For ad videos inside viewport, we need to track impression when inside viewport
 * even if not autoplayed. To track impression event, we need to render Autoplay, since it's
 * using useVideoTracking hook responsible for downloading video.vast_tag and firing impression
 */
const isInViewportAd = (video, isInViewport) => {
    return !!(video.badge === 'ad' && video.vast_tag && isInViewport);
};
/**
 *
 */
export const Thumbnail = (props) => {
    const { appContext, hasPlayingInPlayerOverlay, index, total, video, hasAutoplay, params, isLarge, isSmall, fetchMoreVideos, onFeedImpression, trackThumbnailImpressionsEmbedFeed, showV2Card, responsive, shouldPulse, } = props;
    const isVideoLocked = useVideoIsLocked(video);
    const { t } = useContextTranslation();
    const performance = usePerformance(params);
    const { updateVideoFromLiveStream } = useStore(actionsSelector);
    const videos = useStore(videosSelector);
    const { setAutoplay, pauseAutoplay, registerInViewport, unregisterInViewport, setThumbnailLoaded, registerThumbnailImpression, } = useEmbedFeedStore((state) => state.actions);
    const [isMouseOver, setIsMouseOver] = useState(false);
    const autoplayTimer = useRef(null);
    const isTouched = useRef(false);
    const productCarouselBottomLayout = isTruthy(params.player_deck_layout);
    const [{ isInViewport }, dispatchIntersectionChanged] = useReducer(reducer, {
        index,
        total,
        impressionTimeout: undefined,
        isInViewport: false,
        wasInViewport: false,
        video,
        videos,
        onFeedImpression,
        registerThumbnailImpression,
        registerInViewport,
        unregisterInViewport,
        trackThumbnailImpressionsEmbedFeed,
    });
    const shouldPreloadVideo = !isVideoLocked &&
        (hasAutoplay || isMouseOver || isInViewportAd(video, isInViewport));
    // Preload video
    const videoWrapper = usePreloadVideo({
        video,
        params,
        enabled: shouldPreloadVideo,
    });
    // Subscribe to video play status
    const { isVideoLoaded, isVideoPlaying, wasVideoPlaying } = useVideoPlayStatus(videoWrapper);
    const shouldRenderAutoplay = !isVideoLocked &&
        (hasAutoplay ||
            isVideoLoaded ||
            wasVideoPlaying ||
            isInViewportAd(video, isInViewport));
    const linkRef = useVisibilityObserver({
        onChange: useCallback((isVisible) => dispatchIntersectionChanged({ isVisible, fetchMoreVideos }), [fetchMoreVideos]),
        threshold: [0.5],
    });
    const { onThumbnailClick } = useThumbnailOnClick({
        video,
        appContext,
        params,
    });
    const { isFirstPartyLiveStream } = useFirstPartyLiveStream({
        video,
        appContext,
        params,
    });
    const handleMouseOver = useCallback((e) => {
        const isTouchEnd = e?.type === 'touchend';
        if (!isTouched.current || isTouchEnd) {
            setIsMouseOver(true);
            // ensure this is in viewport and not playing
            if (isInViewport) {
                // ensure mouse is inside with some delay
                debounce(() => {
                    // start autoplay
                    setPlayTrigger(params.widget_id, video.encoded_id, PlayTriggersEnum.PLAY_TRIGGER_HOVERPLAY);
                    setAutoplay(video.encoded_id);
                    customEmbedEvent({
                        type: EventType.FW_EMBEDFEED_THUMBNAILHOVER,
                        params,
                        data: { video },
                    });
                }, THUMBNAIL_AUTOPLAY_DELAY)();
            }
        }
    }, [isInViewport, params, setAutoplay, video]);
    const handleMouseLeave = useCallback(() => {
        setIsMouseOver(false);
        if (params.autoplay_keep_thumbnail) {
            pauseAutoplay();
        }
        // cancel mouse enter timeout if any
        if (autoplayTimer.current) {
            clearTimeout(autoplayTimer.current);
            autoplayTimer.current = null;
        }
    }, [pauseAutoplay, params]);
    const handleOnLoad = useCallback(() => {
        setThumbnailLoaded(video);
        if (index === 0) {
            customEmbedEvent({
                type: EventType.FW_EMBEDFEED_THUMBNAILLOAD,
                params,
                data: {
                    video,
                },
            });
            performance?.measure(KEY_FIRST_THUMBNAIL_RENDERED, KEY_FIRST_VIDEOS_RECEIVED, undefined);
        }
    }, [index, params, performance, setThumbnailLoaded, video]);
    const onUpdateLiveStream = useCallback((props) => {
        updateVideoFromLiveStream(props.liveStreamId, props.liveStream);
    }, [updateVideoFromLiveStream]);
    useLayoutEffect(() => {
        const el = linkRef.current;
        const touchEnd = (e) => {
            handleMouseOver(e);
            isTouched.current = true;
        };
        // We need to add touchend event listener to the element
        // to trigger autoplay on touchend event
        // when in Player Deck
        if (productCarouselBottomLayout) {
            el?.addEventListener('touchend', touchEnd, {
                passive: true,
            });
        }
        // Note: These have to be passive listeners for performance reasons
        // See https://developer.chrome.com/en/docs/lighthouse/best-practices/uses-passive-event-listeners/
        el?.addEventListener('mouseenter', handleMouseOver, { passive: true });
        el?.addEventListener('focusin', handleMouseOver, { passive: true });
        el?.addEventListener('mouseover', handleMouseOver, { passive: true });
        el?.addEventListener('mouseleave', handleMouseLeave, { passive: true });
        el?.addEventListener('focusout', handleMouseLeave, { passive: true });
        return () => {
            if (productCarouselBottomLayout) {
                el?.removeEventListener('touchend', touchEnd);
            }
            el?.removeEventListener('mouseenter', handleMouseOver);
            el?.removeEventListener('focusin', handleMouseOver);
            el?.removeEventListener('mouseover', handleMouseOver);
            el?.removeEventListener('mouseleave', handleMouseLeave);
            el?.removeEventListener('focusout', handleMouseLeave);
        };
    }, [handleMouseLeave, handleMouseOver, linkRef, productCarouselBottomLayout]);
    const label = useThumbnailLabel({
        video,
        index,
        total,
    });
    const autoplayDuration = params?.a11y_limit_previews
        ? WCAG_MOTION_PREVIEW_DURATION_LIMIT_SECONDS
        : undefined;
    // Has interaction and using player deck layout
    const hasInteractionWithPlayerDeckLayout = isFirstPartyLiveStream &&
        productCarouselBottomLayout &&
        (video?.products?.length > 0 || hasCTA(video));
    return (<>
      <UseLiveStreamingLite video={video} apiHost={params.api_host} updateLiveStream={onUpdateLiveStream}/>

      <ThumbnailWrapper className={`cy-thumbnail scrollTo_${video.encoded_id}`} isLarge={isLarge} isSmall={isSmall} mode={params.mode} label={label} showV2Card={showV2Card} productCarouselBottomLayout={productCarouselBottomLayout} hasInteractionWithPlayerDeck={hasInteractionWithPlayerDeckLayout} hasHorizontalLayout={params.thumbnail_orientation === 'horizontal'} responsive={responsive} shouldPulse={shouldPulse}>
        {/* See the comment bellow */}
        {/* eslint-disable-next-line zeffo/no-down-events*/}
        <ThumbnailContainer isClickable={!productCarouselBottomLayout} className={cx('cy-thumbnail-link', { ['sp']: !!video.vast_tag }, {
            ['autoplay']: hasAutoplay,
        })} ref={linkRef} onClick={onThumbnailClick} 
    // this onClick handler is an exception since there is a button nested within this component.
    // we want the onClick to be on the whole component not just the button.
    // todo (CS-2031) figure out why this is needed.
    // do not remove otherwise (see ticket for more info)
    onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
                onThumbnailClick(e);
            }
        }} data-testid={EMBED_THUMBNAIL_CONTAINER}>
          <ThumbnailBody video={video} isLarge={isLarge} isInViewport={isInViewport} isMouseOver={isMouseOver} isVideoLocked={isVideoLocked} isVideoPlaying={isVideoPlaying} params={params} showV2Card={showV2Card}>
            {hasPlayingInPlayerOverlay && (<PlayingInPIPOverlay>
                {t('Playing in picture in picture.')}
              </PlayingInPIPOverlay>)}
            {video.live_stream_status === Enum_LiveStream_Status.PAUSED && (<HostWillBeRightBackOverlay>
                {t('Host will be right back.')}
              </HostWillBeRightBackOverlay>)}
            <ThumbnailPictureByOrientation alt={video?.caption || t('Firework video')} loading={index <= 3 ? 'eager' : 'lazy'} onLoad={handleOnLoad} video={video}/>
            {shouldRenderAutoplay && !params.a11y_disable_previews && (<Suspense fallback={<Fragment />}>
                <Autoplay appContext={appContext} videoId={video.encoded_id} video={video} videoWrapper={videoWrapper} variant={video.variant ?? ''} index={index} hasAutoplay={hasAutoplay} params={params} duration={autoplayDuration}/>
              </Suspense>)}
          </ThumbnailBody>
        </ThumbnailContainer>
      </ThumbnailWrapper>
    </>);
};
